/**
 * Single source of truth for skyboxes and plinth materials used by both the
 * live 3D scene (`BoardCanvas3D`) and the launch wizard (`IdentityStep`).
 *
 * Keeping everything keyed by stable string IDs means saved drafts and
 * existing mock boards don't need migration when the rendering pipeline
 * changes underneath.
 */

import type { SkyboxTheme } from './skyboxes/types';
import type {
  PlinthMaterialId,
  PlinthMaterialSpec,
} from './plinths/types';
import {
  PLINTH_MATERIALS,
  PLINTH_MATERIAL_BY_ID,
} from './plinths/materials';

// ---------------------------------------------------------------------------
// Skybox metadata
// ---------------------------------------------------------------------------

export type SkyboxTier = 'vanilla' | 'premium';

export interface SkyboxSpec {
  id: SkyboxTheme;
  /** Title used by pickers + chat/HUD strings. */
  name: string;
  /** One-line sublabel shown beneath the name. */
  sublabel: string;
  /** Access tier — only `'vanilla'` is surfaced today. */
  tier: SkyboxTier;

  /**
   * Equirectangular HDRI (LDR JPG is fine). Path is under `/public`, served
   * from the web root. If the asset is missing the skybox transparently
   * falls back to the shader-only render path; no error is surfaced to the
   * user. `null` means "never try — always use the shader".
   */
  hdriUrl: string | null;

  /** CSS background used by the launch wizard picker thumbnails. */
  thumbnail: string;

  /**
   * Ambient key/rim tints used by directional lights in `BoardCanvas3D`
   * when the scene has no environment map (lite tier). Preserved from the
   * previous hardcoded switch blocks so lighting doesn't jump when we ship
   * the new renderer.
   */
  keyLight: string;
  rimLight: string;
}

export const SKYBOXES: readonly SkyboxSpec[] = [
  {
    id: 'sky',
    name: 'Open Sky',
    sublabel: 'Cerulean day · puffy clouds',
    tier: 'vanilla',
    hdriUrl: null,
    thumbnail:
      'linear-gradient(180deg, #0a4a9c 0%, #2e8ae0 28%, #7dd0ff 55%, #c8f0ff 85%, #f2fbff 100%), radial-gradient(ellipse 55% 38% at 78% 20%, rgba(255,255,255,0.75), transparent 55%)',
    keyLight: '#bfe4ff',
    rimLight: '#4a7ec0',
  },
  {
    id: 'aurora',
    name: 'Aurora',
    sublabel: 'Green & violet sheets · moons',
    tier: 'vanilla',
    hdriUrl: null,
    thumbnail:
      'linear-gradient(200deg, #0a0f1a 0%, #0e1a2e 30%, #1a2a4a 55%, #0d1520 100%), linear-gradient(95deg, transparent 0%, rgba(0,255,120,0.45) 22%, rgba(180,40,255,0.4) 48%, rgba(0,255,100,0.35) 72%, transparent 100%), radial-gradient(ellipse 35% 28% at 30% 20%, rgba(200,220,255,0.45), transparent 50%)',
    keyLight: '#6a8ab8',
    rimLight: '#2d4868',
  },
  {
    id: 'sci-fi',
    name: 'Nebula',
    sublabel: 'Violet haze · rift · stars',
    tier: 'vanilla',
    hdriUrl: null,
    thumbnail:
      'radial-gradient(ellipse 80% 55% at 20% 25%, rgba(255,110,200,0.5) 0%, transparent 50%), radial-gradient(ellipse 70% 50% at 80% 35%, rgba(100,200,255,0.4) 0%, transparent 48%), linear-gradient(180deg, #050210 0%, #180828 50%, #040208 100%)',
    keyLight: '#f0d5ff',
    rimLight: '#8855b8',
  },
  {
    id: 'medieval',
    name: 'Dusk',
    sublabel: 'Amber line · mountain ridge · embers',
    tier: 'vanilla',
    hdriUrl: null,
    thumbnail:
      'linear-gradient(180deg, #0c0a0e 0%, #1e1420 28%, #4a1c28 48%, #7a3a1a 62%, #1a100c 100%)',
    keyLight: '#c8a060',
    rimLight: '#4a3a32',
  },
  {
    id: 'farm',
    name: 'Homestead',
    sublabel: 'Valley wood · long sunset',
    tier: 'vanilla',
    hdriUrl: null,
    thumbnail:
      'linear-gradient(180deg, #1a0a1e 0%, #4a1a3a 15%, #ff8a4a 35%, #ffb070 50%, #3d2a1a 78%, #1a0f0a 100%)',
    keyLight: '#ffd8b0',
    rimLight: '#e88850',
  },
  {
    id: 'desert',
    name: 'Desert',
    sublabel: 'Azure sky · dune floor',
    tier: 'vanilla',
    hdriUrl: null,
    thumbnail:
      'linear-gradient(180deg, #0a4a7a 0%, #2e8ae0 30%, #7ec8ff 60%, #e0c898 100%), radial-gradient(ellipse 50% 35% at 75% 18%, rgba(255,255,255,0.5), transparent 50%)',
    keyLight: '#d8e8f8',
    rimLight: '#b89868',
  },
  // 1-of-1 boardroom mark — `tier: 'premium'` filters it out of the
  // launch wizard's `PUBLIC_SKYBOX_PRESETS` list so community boards
  // can't pick it. The Boardroom (HV grid 0) sets `skybox: 'sanctum'`
  // explicitly in `mock-boards.ts` and the dev `SkyboxPickerPanel`
  // still surfaces it for QA preview on any board.
  {
    id: 'sanctum',
    name: 'Sanctum',
    sublabel: 'Crimson sigil · cabal chamber',
    tier: 'premium',
    hdriUrl: null,
    thumbnail:
      'radial-gradient(ellipse 30% 22% at 50% 18%, rgba(216,48,72,0.65) 0%, rgba(140,32,80,0.25) 35%, transparent 65%), radial-gradient(ellipse 75% 28% at 50% 92%, rgba(180,20,30,0.55) 0%, transparent 60%), linear-gradient(180deg, #0a020e 0%, #150514 45%, #1a0608 80%, #050204 100%)',
    keyLight: '#a83048',
    rimLight: '#3a1240',
  },
];

export const SKYBOX_BY_ID: Record<SkyboxTheme, SkyboxSpec> = SKYBOXES.reduce(
  (acc, s) => ({ ...acc, [s.id]: s }),
  {} as Record<SkyboxTheme, SkyboxSpec>,
);

/**
 * Perceptual brightness of each skybox as a design constant.
 *
 * Open Sky, Homestead, and Desert (day / bright arid) are bright for HUD.
 * Aurora, Nebula, and Dusk are dimmer so card contrast stays in band.
 *
 * Exposed as data (not a class string) so different panels can pick
 * the level of bump that matches their visual weight.
 */
const BRIGHT_SKYBOXES: ReadonlySet<SkyboxTheme> = new Set(['sky', 'farm', 'desert']);

export function isBrightSkybox(theme: SkyboxTheme | null | undefined): boolean {
  return theme ? BRIGHT_SKYBOXES.has(theme) : false;
}

/**
 * Returns the spec for a skybox, falling back to `sky` (open clear-day default)
 * if the ID isn't recognised. Callers should treat
 * this as total to avoid null-handling noise at call sites.
 */
export function skyboxSpec(id: SkyboxTheme | string | null | undefined): SkyboxSpec {
  if (id && id in SKYBOX_BY_ID) return SKYBOX_BY_ID[id as SkyboxTheme];
  return SKYBOX_BY_ID.sky;
}

// ---------------------------------------------------------------------------
// Plinth material defaults per skybox
// ---------------------------------------------------------------------------

/**
 * Default plinth material used when a board/draft has no explicit choice.
 * Tuned to match the plinth palette shipped in the previous renderer so
 * existing mock boards and user drafts keep their current vibe.
 */
export const DEFAULT_MATERIAL_FOR_SKYBOX: Record<SkyboxTheme, PlinthMaterialId> = {
  aurora: 'marble',
  'sci-fi': 'obsidian',
  medieval: 'stone',
  sky: 'marble',
  farm: 'wood',
  desert: 'sandstone',
  // 1-of-1 emerald velvet drape with gold tassels at the corners.
  // Pairs with the Sanctum sky to complete the cabal-chamber identity;
  // velvet is `tier: 'premium'` so community boards can't pick it even
  // if they could somehow select the sanctum sky.
  sanctum: 'velvet',
};

/**
 * Resolves the plinth material for a given (skybox, user-choice) pair.
 * Returns the user's choice if valid, otherwise the skybox's default.
 */
export function resolveMaterial(
  skybox: SkyboxTheme,
  chosen?: PlinthMaterialId | null,
): PlinthMaterialSpec {
  if (chosen && chosen in PLINTH_MATERIAL_BY_ID) {
    return PLINTH_MATERIAL_BY_ID[chosen];
  }
  return PLINTH_MATERIAL_BY_ID[DEFAULT_MATERIAL_FOR_SKYBOX[skybox] ?? 'stone'];
}

// ---------------------------------------------------------------------------
// Re-exports for pickers / consumers
// ---------------------------------------------------------------------------

export { PLINTH_MATERIALS, PLINTH_MATERIAL_BY_ID } from './plinths/materials';
export type {
  PlinthMaterialId,
  PlinthMaterialSpec,
  PlinthMaterialTier,
} from './plinths/types';
export type { SkyboxTheme } from './skyboxes/types';
export { SKYBOX_THEMES } from './skyboxes/types';

export {
  SKYBOX_BRIEFS,
  type SkyboxDesignBrief,
} from './skybox-briefs';
